/**
 * Application state management for Ragsmith Web UI
 */

// Use the same host the page was loaded from (works for localhost, IP, or domain)
export const API_BASE = `${window.location.protocol}//${window.location.host}`;

// Core state object
export const state = {
    sessionId: null,
    currentAgent: '',
    currentCollection: '',
    isStreaming: false,
    loadedAgent: null,
    loadedCollection: null,
    loadedCollectionTitle: null,  // Human-friendly title for display
    loadedCollectionDescription: null,  // Collection description for display
    loadedModel: null,  // Currently loaded model tier (in VRAM)
    loadedProvider: null,  // Provider of loaded model ('local', 'groq', 'anthropic', 'openrouter')
    selectedModel: null,  // Model selected in UI but not yet loaded
    selectedModelData: null,  // Full model data for selectedModel (for loading)
    isReasoningModel: false,  // Whether the loaded model outputs thinking content

    // Narrator mode state
    narratorMode: false,
    narratorPaused: false,   // true = narrator position preserved but chat is normal
    narratorState: {
        book: null,
        kb_path: null,
        chunk_id: 1,
        chunk_batch: 1,       // Verbatim: how many chunks per page (1-50)
        narration_length: 1   // Non-verbatim: 0=Short(1), 1=Medium(3), 2=Long(5), 3=Very Long(10)
    },

    // Field collection state (for required fields before initial prompt)
    pendingFields: [],
    agentSettings: null,
    agentKbCount: 0,  // Number of KBs in loaded agent/collection
    isLightAgent: false,  // Light agents skip citations (single KB)

    // Instruction field state (for trigger-based data collection)
    pendingInstruction: null,

    // Temperature setting (null = use server default) - persisted to localStorage
    temperature: (() => {
        const saved = localStorage.getItem('ragsmith_temperature');
        return saved !== null ? parseFloat(saved) : null;
    })(),

    // Fidelity setting (0.0-1.0) - controls how strictly LLM adheres to KB sources
    // 0.0 = creative (can supplement), 1.0 = strict (only use KB context)
    fidelity: (() => {
        const saved = localStorage.getItem('ragsmith_fidelity');
        return saved !== null ? parseFloat(saved) : 0.7;  // Default to strict-ish
    })(),

    // Web search depth: "off", "surface", "shallow", "deep"
    // "off" = disabled, "surface" = snippets only, "shallow" = LLM picks + fetch, "deep" = + summarize
    webSearchDepth: localStorage.getItem('ragsmith_web_search_depth') || 'off',

    // Legacy compat - derived from depth
    get webSearchEnabled() {
        return this.webSearchDepth !== 'off';
    },

    // Debug mode - shows routing/search info - persisted to localStorage
    debugMode: localStorage.getItem('ragsmith_debug') === 'true',

    // Memory level: 0 = Off, 1 = Basic (context window only), 2 = Full (context + fact extraction)
    memoryLevel: (() => {
        const saved = localStorage.getItem('ragsmith_memory_level');
        return saved !== null ? parseInt(saved, 10) : 2;  // Default to Full
    })(),

    // Auto-compact: fold older turns into a summary between turns once the
    // context fills (src/core/compaction.py). On unless switched off (persisted)
    autoCompact: localStorage.getItem('ragsmith_auto_compact') !== 'false',

    // Image search: when enabled alongside web search, also returns image results (persisted)
    imageSearchEnabled: localStorage.getItem('ragsmith_image_search') === 'true',

    // Playback: whether TTS play buttons appear on assistant messages (persisted)
    playbackEnabled: localStorage.getItem('ragsmith_playback') !== 'false',  // ON by default

    // Can anything actually serve voice right now? Set from /voice/status at
    // startup. playbackEnabled is a PREFERENCE; these are FACTS about what
    // can serve.
    //
    // The two halves are tracked SEPARATELY because they genuinely differ
    // now: cloud lanes are switched on independently, and a worker can hold
    // Whisper while Kokoro's VRAM goes to an LLM. "Talk to it, read the
    // reply" is a supported arrangement, not a broken one.
    // null = not asked yet, treated as available so nothing flickers off
    // during startup.
    voiceAvailable: null,   // microphone / transcription (STT)
    ttsAvailable: null,     // read-aloud (TTS)

    // Dictation: voice-button captures Whisper transcript instead of starting voice chat (persisted)
    dictationMode: localStorage.getItem('ragsmith_dictation_mode') === 'true',  // OFF by default
    // Dictation extract facts: run extract_facts on saved dictation message (persisted)
    dictationExtractFacts: localStorage.getItem('ragsmith_dictation_extract') === 'true',  // OFF by default

    // Rate limit / retry state (not persisted)
    consecutiveFailures: 0,
    cooldownUntil: null,  // Timestamp when cooldown ends
    lastSentMessage: null,  // { message, attachments } for retry
    lastUserMessageEl: null,  // DOM element of last user message (for removal on retry)

    // Tool toggles - per-session disabled tools (not persisted)
    disabledTools: new Set(),

    // Meta agents: per-subordinate disabled tools {agentName: Set(toolName)}
    subordinateDisabledTools: {},

    // Planning toggle (per-session, not persisted)
    planningEnabled: true,  // true when agent has use_planning and user hasn't toggled off

    // Container mode state (not persisted - URL-driven)
    containerMode: false,
    containerToken: null,
    containerConfig: null,  // { agent, collection, model } from validated token

    // Capsule mode state (shareable conversation links)
    capsuleMode: false,
    capsuleToken: null,
    capsuleConfig: null  // { session_id, allow_prompts, allow_voice, agent, model, label }
};

// State mutation helpers
export function setTemperature(value) {
    state.temperature = value;
    if (value === null) {
        localStorage.removeItem('ragsmith_temperature');
    } else {
        localStorage.setItem('ragsmith_temperature', value.toString());
    }
}

export function setDebugMode(enabled) {
    state.debugMode = enabled;
    localStorage.setItem('ragsmith_debug', enabled.toString());
}

export function setFidelity(value) {
    state.fidelity = value;
    if (value === null) {
        localStorage.removeItem('ragsmith_fidelity');
    } else {
        localStorage.setItem('ragsmith_fidelity', value.toString());
    }
}

export function setWebSearchDepth(depth) {
    state.webSearchDepth = depth;
    localStorage.setItem('ragsmith_web_search_depth', depth);
}

export function setMemoryLevel(level) {
    state.memoryLevel = level;
    localStorage.setItem('ragsmith_memory_level', level.toString());
}

export function setAutoCompact(enabled) {
    state.autoCompact = enabled;
    localStorage.setItem('ragsmith_auto_compact', enabled.toString());
}

export function setPlaybackEnabled(enabled) {
    state.playbackEnabled = enabled;
    localStorage.setItem('ragsmith_playback', enabled.toString());
}

export function togglePlayback() {
    setPlaybackEnabled(!state.playbackEnabled);
    return state.playbackEnabled;
}

export function toggleDictationMode() {
    state.dictationMode = !state.dictationMode;
    localStorage.setItem('ragsmith_dictation_mode', state.dictationMode.toString());
    return state.dictationMode;
}

export function toggleDictationExtractFacts() {
    state.dictationExtractFacts = !state.dictationExtractFacts;
    localStorage.setItem('ragsmith_dictation_extract', state.dictationExtractFacts.toString());
    return state.dictationExtractFacts;
}

/**
 * Apply a container's default chat input mode ('prompt' | 'dictation' | 'extract').
 * Maps the mode string to the two dictation booleans and persists them, so the
 * container's intended default takes effect on load (the user can still toggle after).
 * No-op for a falsy mode (no container default → keep the user's own preference).
 */
export function applyContainerInputMode(mode) {
    if (!mode) return;
    const dictation = mode === 'dictation' || mode === 'extract';
    const extract = mode === 'extract';
    state.dictationMode = dictation;
    state.dictationExtractFacts = extract;
    localStorage.setItem('ragsmith_dictation_mode', dictation.toString());
    localStorage.setItem('ragsmith_dictation_extract', extract.toString());
}

export function toggleImageSearch() {
    state.imageSearchEnabled = !state.imageSearchEnabled;
    localStorage.setItem('ragsmith_image_search', state.imageSearchEnabled.toString());
    return state.imageSearchEnabled;
}

// Cycle through search depths: off -> surface -> shallow -> deep -> off
export function cycleWebSearchDepth() {
    const depths = ['off', 'surface', 'shallow', 'deep'];
    const currentIndex = depths.indexOf(state.webSearchDepth);
    const nextIndex = (currentIndex + 1) % depths.length;
    setWebSearchDepth(depths[nextIndex]);
    return depths[nextIndex];
}

/** Toggle a tool on/off. Returns true if now enabled, false if disabled. */
export function toggleTool(name) {
    if (state.disabledTools.has(name)) {
        state.disabledTools.delete(name);
        return true;
    } else {
        state.disabledTools.add(name);
        return false;
    }
}

/** Reset all tool toggles (called on agent load/switch). */
export function resetDisabledTools() {
    state.disabledTools.clear();
    state.subordinateDisabledTools = {};
    state.planningEnabled = true;
}

/** Meta agents: toggle a tool for one subordinate. Returns true if now enabled. */
export function toggleSubordinateTool(agentName, toolName) {
    const set = state.subordinateDisabledTools[agentName] || (state.subordinateDisabledTools[agentName] = new Set());
    if (set.has(toolName)) {
        set.delete(toolName);
        return true;
    }
    set.add(toolName);
    return false;
}

/** Meta agents: {agent: [tool, ...]} for the chat request, or null when empty. */
export function getSubordinateDisabledTools() {
    const out = {};
    for (const [agentName, set] of Object.entries(state.subordinateDisabledTools)) {
        if (set.size) out[agentName] = [...set];
    }
    return Object.keys(out).length ? out : null;
}

/** Toggle planning on/off. Returns new state (true = enabled). */
export function togglePlanning() {
    state.planningEnabled = !state.planningEnabled;
    return state.planningEnabled;
}

export function resetNarratorState() {
    state.narratorMode = false;
    state.narratorPaused = false;
    state.narratorState = { book: null, kb_path: null, chunk_id: 1, chunk_batch: 1, narration_length: 1 };
    // Update web search button (re-enabled when narrator mode off)
    window.updateWebSearchButtonState?.();
    window.updateNarratorMenuSection?.();
    // Persist cleared state to sessionStorage so refresh doesn't restore stale narrator mode
    if (state.loadedModel) {
        saveSessionStateWithModel(state.loadedModel);
    }
}

// Narrator progress storage key prefix
const NARRATOR_PROGRESS_PREFIX = 'ragsmith_narrator_';

/**
 * Save narrator progress for a specific session to localStorage
 * @param {string} sessionId - The session ID
 * @param {object} narratorState - Narrator state to save
 */
export function saveNarratorProgress(sessionId, narratorState) {
    if (!sessionId || !narratorState) return;
    try {
        localStorage.setItem(
            NARRATOR_PROGRESS_PREFIX + sessionId,
            JSON.stringify(narratorState)
        );
    } catch (e) {
        console.error('[State] Failed to save narrator progress:', e);
    }
}

/**
 * Load narrator progress for a specific session from localStorage
 * @param {string} sessionId - The session ID
 * @returns {object|null} Narrator state or null if not found
 */
export function loadNarratorProgress(sessionId) {
    if (!sessionId) return null;
    try {
        const saved = localStorage.getItem(NARRATOR_PROGRESS_PREFIX + sessionId);
        if (saved) {
            return JSON.parse(saved);
        }
    } catch (e) {
        console.error('[State] Failed to load narrator progress:', e);
    }
    return null;
}

/**
 * Clear narrator progress for a specific session
 * @param {string} sessionId - The session ID
 */
export function clearNarratorProgress(sessionId) {
    if (!sessionId) return;
    try {
        localStorage.removeItem(NARRATOR_PROGRESS_PREFIX + sessionId);
    } catch (e) {
        console.error('[State] Failed to clear narrator progress:', e);
    }
}

export function resetSession() {
    state.sessionId = null;
    state.loadedAgent = null;
    state.loadedCollection = null;
    resetNarratorState();
    clearSessionStorage();
}

// Session storage key
const SESSION_STORAGE_KEY = 'ragsmith_session';

/**
 * Save current session state to sessionStorage
 */
export function saveSessionState() {
    const sessionState = {
        sessionId: state.sessionId,
        loadedAgent: state.loadedAgent,
        loadedCollection: state.loadedCollection,
        modelTier: null, // Will be set by caller
        narratorMode: state.narratorMode,
        narratorPaused: state.narratorPaused,
        narratorState: state.narratorState
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionState));
}

/**
 * Save session state with model tier
 */
export function saveSessionStateWithModel(modelTier) {
    const sessionState = {
        sessionId: state.sessionId,
        loadedAgent: state.loadedAgent,
        loadedCollection: state.loadedCollection,
        loadedCollectionTitle: state.loadedCollectionTitle,
        modelTier: modelTier || null,
        loadedProvider: state.loadedProvider || null,
        narratorMode: state.narratorMode,
        narratorPaused: state.narratorPaused,
        narratorState: state.narratorState,
        containerToken: state.containerToken || null
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionState));
}

/**
 * Load session state from sessionStorage
 */
export function loadSessionState() {
    try {
        const saved = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (saved) {
            return JSON.parse(saved);
        }
    } catch (e) {
        console.error('[State] Failed to load session state:', e);
    }
    return null;
}

/**
 * Clear session storage
 */
export function clearSessionStorage() {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
}

// Rate limit / retry helpers
export const COOLDOWN_MS = 60000;  // 60 second cooldown
const FAILURE_THRESHOLD = 3;  // Trigger cooldown after 3 consecutive failures

/**
 * Record a failure. Returns true if cooldown was triggered.
 */
export function recordFailure() {
    state.consecutiveFailures++;
    if (state.consecutiveFailures >= FAILURE_THRESHOLD) {
        state.cooldownUntil = Date.now() + COOLDOWN_MS;
        return true;
    }
    return false;
}

/**
 * Record a success - resets failure counter
 */
export function recordSuccess() {
    state.consecutiveFailures = 0;
    // Don't clear cooldownUntil - let it expire naturally
}

/**
 * Clear cooldown and reset failure counter (e.g. when switching billing mode)
 */
export function clearCooldown() {
    state.cooldownUntil = null;
    state.consecutiveFailures = 0;
}

/**
 * Check if we're in cooldown. Returns remaining ms or 0 if not in cooldown.
 */
export function getCooldownRemaining() {
    if (!state.cooldownUntil) return 0;
    const remaining = state.cooldownUntil - Date.now();
    if (remaining <= 0) {
        state.cooldownUntil = null;
        state.consecutiveFailures = 0;  // Reset after cooldown expires
        return 0;
    }
    return remaining;
}

/**
 * Store the last sent message for retry
 */
export function setLastSentMessage(message, attachments, userMessageEl) {
    state.lastSentMessage = { message, attachments: attachments || [] };
    state.lastUserMessageEl = userMessageEl;
}

/**
 * Clear the last sent message (after successful response or new message sent)
 */
export function clearLastSentMessage() {
    state.lastSentMessage = null;
    state.lastUserMessageEl = null;
}

/**
 * Set container mode state
 */
export function setContainerMode(token, config) {
    state.containerMode = true;
    state.containerToken = token;
    state.containerConfig = config;
    // Also set loaded state so header displays correctly
    if (config.agent) {
        state.loadedAgent = config.agent;
        state.currentAgent = config.agent;
    }
    if (config.model) {
        state.loadedModel = config.model;
    }
    if (config.collection) {
        state.loadedCollection = config.collection;
    }
    // Apply the container's default chat input mode, if the admin set one.
    applyContainerInputMode(config.default_input_mode);
}

/**
 * Check if container mode is active
 */
export function isContainerMode() {
    return state.containerMode === true;
}

/**
 * Clear container mode state and all persisted container data.
 * Used when the user explicitly leaves a container session.
 */
export function clearContainerMode() {
    state.containerMode = false;
    state.containerToken = null;
    state.containerConfig = null;
    sessionStorage.removeItem('ragsmith_container');
    localStorage.removeItem('ragsmith_container_token');
}

/**
 * Set capsule mode state
 */
export function setCapsuleMode(token, config) {
    state.capsuleMode = true;
    state.capsuleToken = token;
    state.capsuleConfig = config;
    if (config.agent) {
        state.loadedAgent = config.agent;
        state.currentAgent = config.agent;
    }
    if (config.model) {
        state.loadedModel = config.model;
    }
    if (config.collection) {
        state.loadedCollection = config.collection;
    }
    if (config.session_id) {
        state.sessionId = config.session_id;
    }
}

/**
 * Check if capsule mode is active
 */
export function isCapsuleMode() {
    return state.capsuleMode === true;
}

/**
 * Clear capsule mode state
 */
export function clearCapsuleMode() {
    state.capsuleMode = false;
    state.capsuleToken = null;
    state.capsuleConfig = null;
    sessionStorage.removeItem('ragsmith_capsule');
}
